import { useMemo, useEffect, useRef, useState } from 'react';
import cm from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { StoryBoardTracks, setCellResizeHighlight } from 'components/app/storyboard/_mediator/sb.slice';
import { useCellResize } from './_use-cell-resize';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { TooltipHelp } from 'components/UI/tooltip/tooltip-help';

import styles from './_sb-cell-resize.module.scss';

const TIMEOUT: Milliseconds = 600;
const MIN_RESIZE_SIZE = 50;

interface Props {
  clip: Clip | null;
  track: StoryBoardTracks;
  cell: Index;
  dnd: boolean;
}

export const SbCellResize = ({ clip, track, cell, dnd }: Props) => {
  const { resetHoveredClip } = useClipThumbPrevContext();
  const linkedIndex = useAppSelector((state) => clip && state.sb.sbClipPlaybackMap[clip.clipId]?.linkedIndex);
  const cellResizeHighlight = useAppSelector((state) => state.sb.cellResizeHighlight);
  const isTouchDevice = useAppSelector((state) => state.app.isTouchDevice);
  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);
  const snapToBeats = useAppSelector((state) => state.editor.storyConfig?.snapToBeats);
  const zoomTracks = useAppSelector((state) => state.app.zoomTracks);
  const markers = useAppSelector((state) => state.editor.markers);
  const dragger = useAppSelector((state) => state.app.dragger);
  const helpMode = useAppSelector(selectHelpMode);
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const dispatch = useAppDispatch();

  const { container, snapLabel, leftHandle, rightHandle, onResizeStart, onResizeEnd, outsideHandle } = useCellResize(
    clip,
    cell,
    track
  );

  const [smallCell, setSmallCell] = useState(false);
  const [hover, setHover] = useState(false);

  const handles = useMemo(() => {
    if (!clip || !markers) return [];
    const res = ['', ''];

    if (clip.startTime !== null && clip.endTime)
      res[0] = res[1] = 'Clip Resize not supported when both start and end tags are defined';

    if (linkedIndex) {
      res[0] = res[1] = 'Clip Resize not supported on interior linked clips';
      if (linkedIndex.isFirstInGroup)
        res[0] =
          clip.startTime === null ? '' : 'Clip Resize not supported when start tag is defined on first linked clip';
      if (linkedIndex.isLastInGroup)
        res[1] = clip.endTime === null ? '' : 'Clip Resize not supported when end tag is defined on last linked clip';
    }
    if (beginnerMode || snapToBeats) {
      if (cell === 0) res[0] = 'Clip Resize not supported at beginning of track';
      if (cell === markers.length) res[1] = 'Clip Resize not supported at end of track';
    }
    return res;
  }, [clip, linkedIndex, beginnerMode, snapToBeats, markers]);

  const onMouseLeave = () => {
    if (dragger === EnumDragger.cell) return;
    setHover(false);
  };

  useEffect(() => {
    return () => clearTimeout(timeoutId.current);
  }, []);

  useEffect(() => {
    if (zoomTracks || !container.current) return;
    setSmallCell(container.current.clientWidth < MIN_RESIZE_SIZE);
  }, [zoomTracks]);

  useEffect(() => {
    if (dragger !== null) return;
    if (!outsideHandle.current) return;
    resetHoveredClip();
    setHover(false);
  }, [dragger]);

  useEffect(() => {
    clearTimeout(timeoutId.current);
    if (hover) {
      if (isTouchDevice) dispatch(setCellResizeHighlight(true));
      else timeoutId.current = setTimeout(() => dispatch(setCellResizeHighlight(true)), TIMEOUT);
    } else if (cellResizeHighlight) setTimeout(() => dispatch(setCellResizeHighlight(false)), 22);
  }, [hover]);

  useEffect(() => {
    setHover(false);
  }, [handles]);

  if (dnd) return null;

  return (
    <div
      ref={container}
      className={cm(styles.SbCellResize, {
        [styles['SbCellResize--hover']]: hover || isTouchDevice,
        [styles['SbCellResize--touchDevice']]: isTouchDevice,
        [styles['SbCellResize--hidden']]: smallCell || zoomTracks,
      })}
    >
      {snapLabel && <div className={styles.SbCellResize__snapLabel} style={{ left: snapLabel + 'px' }} />}
      {handles.map((handle, index) => {
        if (!helpMode && !!handle) return null;
        const key = index === 1 ? 'right' : 'left';
        return (
          <Tooltip
            key={index}
            showTooltip={helpMode && !!handle}
            position="top"
            dynamicPosition
            tip={
              <TooltipHelp className={styles.Config__tooltip}>
                <p>{handle}</p>
              </TooltipHelp>
            }
          >
            <div
              ref={index === 1 ? rightHandle : leftHandle}
              onMouseLeave={onMouseLeave}
              onMouseEnter={() => setHover(true)}
              onPointerDown={(e) => !handle && onResizeStart(e, key)}
              onPointerUp={(e) => !handle && onResizeEnd(e, key)}
              onClick={(e) => e.stopPropagation()}
              className={cm(styles.SbCellResize__handle, styles[`SbCellResize__handle--${key}`], {
                [styles['SbCellResize__handle--alert']]: handle,
              })}
            />
          </Tooltip>
        );
      })}
    </div>
  );
};
